/**
 * Compact delivery address autocomplete for Seafreight FCL calculator.
 * Uses Geoapify autocomplete; on select returns lat/lon for distance calculation.
 */
import React, { useState, useEffect, useRef } from "react";
import { Input } from "antd";
import {
  canSpendCredits,
  recordCreditsUsed,
  GEOCODING_CREDITS,
} from "../../utils/geoapifyCreditTracker";

const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY;

export interface GeoapifyPrediction {
  lat: number;
  lon: number;
  formatted: string;
  street?: string;
  housenumber?: string;
  postcode?: string;
  city?: string;
  country?: string;
}

export interface DeliveryAddressAutocompleteProps {
  value?: string;
  onChange?: (prediction: GeoapifyPrediction | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
}

export const DeliveryAddressAutocomplete: React.FC<
  DeliveryAddressAutocompleteProps
> = ({ value = "", onChange, placeholder, disabled, size = "small" }) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<GeoapifyPrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!inputValue || inputValue.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!apiKey || apiKey === "your_geoapify_api_key_here") {
        setPredictions([]);
        setLoading(false);
        return;
      }
      if (!canSpendCredits(GEOCODING_CREDITS)) {
        setPredictions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(inputValue)}&format=json&limit=5&apiKey=${apiKey}`,
        );
        const data = await res.json();
        const results = data?.results ?? [];
        recordCreditsUsed(GEOCODING_CREDITS);
        setPredictions(
          results.map((r: { lat?: number; lon?: number; formatted?: string; street?: string; housenumber?: string; postcode?: string; city?: string; country?: string }) => ({
            lat: r.lat ?? 0,
            lon: r.lon ?? 0,
            formatted: r.formatted ?? "",
            street: r.street,
            housenumber: r.housenumber,
            postcode: r.postcode,
            city: r.city,
            country: r.country,
          })),
        );
        setShowDropdown(results.length > 0);
      } catch (err) {
        console.error("Geoapify autocomplete error:", err);
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  const handleSelect = (p: GeoapifyPrediction) => {
    setInputValue(p.formatted);
    setShowDropdown(false);
    setPredictions([]);
    onChange?.(p);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <Input
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (!e.target.value) onChange?.(null);
        }}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        allowClear
        onFocus={() => predictions.length > 0 && setShowDropdown(true)}
      />
      {showDropdown && predictions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            margin: 0,
            padding: 0,
            listStyle: "none",
            background: "white",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 1050,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          {predictions.map((p, i) => (
            <li
              key={i}
              onClick={() => handleSelect(p)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: 12,
                borderBottom:
                  i < predictions.length - 1
                    ? "1px solid #f0f0f0"
                    : undefined,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "white";
              }}
            >
              {p.formatted}
            </li>
          ))}
        </ul>
      )}
      {loading && (
        <span
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 10,
            color: "#999",
          }}
        >
          ...
        </span>
      )}
    </div>
  );
};
